"use client";

import { type SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  formatPhoneNumberIntl,
  isValidPhoneNumber,
  parsePhoneNumber,
} from "react-phone-number-input/input";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useMemo } from "react";
import { Icon } from "@iconify-icon/react";

import { selectAccount, sendCommunicationData } from "@/features/auth/api/onboarding";
import useAuthStore from "@/features/auth/store/useAuthStore";
import errorHandler, { assignApiErrorsToFormFields } from "@/features/auth/utils/errorHandler";
import { detectUserCountry } from "@/features/auth/utils/detectCountry";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PhoneInput } from "@/components/PhoneInput/PhoneInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface CommunicationDataFormProps {
  goToNextStep: () => void;
  goToPrevStep: () => void;
}

const CommunicationDataForm = ({ goToNextStep, goToPrevStep }: CommunicationDataFormProps) => {
  const { t } = useTranslation(["forms", "common", "auth"]);
  const {
    onboardingData: {
      phoneNumber: statePhoneNumber,
      secondaryEmail,
      accountType,
      firstName,
      birthDate,
      lastName,
    },
  } = useAuthStore();
  const setOnboardingData = useAuthStore((state) => state.setOnboardingData);

  // We no longer branch UI for under-16 here; show standard fields for everyone

  const communicationDataFormSchema = useMemo(() => {
    return z.object({
      phoneNumber: z
        .string()
        .min(1, { message: t("forms:errors.invalidPhoneNumber") })
        .refine(isValidPhoneNumber, { message: t("forms:errors.invalidPhoneNumber") }),
      secondaryEmail: z.string().email(t("forms:errors.invalidEmail")).or(z.literal("")).optional(),
    });
  }, [i18n.language, t]);

  type CommunicationDataFormValues = z.infer<typeof communicationDataFormSchema>;

  const form = useForm<CommunicationDataFormValues>({
    resolver: zodResolver(communicationDataFormSchema),
    mode: "onTouched",
    defaultValues: {
      phoneNumber: statePhoneNumber?.replaceAll(" ", "") ?? "",
      secondaryEmail: secondaryEmail ?? "",
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    setError,
  } = form;

  const onSubmit: SubmitHandler<CommunicationDataFormValues> = async (data) => {
    if (statePhoneNumber && firstName && lastName && lastName && accountType) {
      await selectAccount(accountType);
      setOnboardingData({ firstName, lastName, birthDate });
    } else if (statePhoneNumber) {
      form.setError("phoneNumber", { message: t("forms:errors.retry") });
    }
    try {
      const parsedPhoneNumber = parsePhoneNumber(data.phoneNumber);

      if (!parsedPhoneNumber) return;

      const phoneNumberCountry = parsedPhoneNumber.country;
      const nationalPhoneNumber = parsedPhoneNumber.nationalNumber;

      if (!phoneNumberCountry) return;

      const res = await sendCommunicationData({
        phoneNumber: nationalPhoneNumber,
        phoneNumberCountry,
        secondaryEmail: data.secondaryEmail,
      });

      setOnboardingData({
        phoneNumber: formatPhoneNumberIntl(parsedPhoneNumber.number),
        secondaryEmail: data.secondaryEmail,
      });

      console.log(res);

      goToNextStep();
    } catch (error: any) {
      const apiErrors = errorHandler<
        "phone_number" | "phone_number_country" | "support_email" | "phone_number_exists"
      >(error);

      if (error.response.data.code === "phone_number_exists") {
        form.setError("phoneNumber", { message: t("forms:errors.phoneNumberExists") });
      }

      assignApiErrorsToFormFields(apiErrors, setError, {
        phoneNumber: "phone_number",
        secondaryEmail: "support_email",
      });
    }
  };

  return (
    <Form {...form}>
      <form className="grid gap-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-4 gap-y-6">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="flex flex-col items-start">
                <FormLabel className="text-left">
                  {t("forms:labels.phoneNumber", "Phone Number")}
                  <span className="text-destructive ml-1">*</span>
                </FormLabel>
                <FormControl className="w-full">
                  <PhoneInput
                    required
                    countryCallingCodeEditable={false}
                    defaultCountry={detectUserCountry()}
                    international={true}
                    placeholder={t("forms:labels.enterPhoneNumber")}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={control}
            name="secondaryEmail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("forms:labels.secondaryEmail", "Secondary Email Address")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      className="bg-side-accent hover:bg-muted/30 focus:bg-muted/40 text-sm backdrop-blur-sm transition-all duration-200"
                      disabled={isSubmitting}
                      placeholder=""
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid gap-4">
          <div className={"flex w-full gap-2"}>
            <Button
              className={
                "bg-muted/20 hover:bg-muted/30 border-border/50 w-20 shadow-lg backdrop-blur-sm"
              }
              disabled={isSubmitting}
              size="lg"
              type="button"
              variant={"outline"}
              onClick={goToPrevStep}
            >
              <Icon icon={"mdi:arrow-left"} />
              {t("common:back")}
            </Button>
            <Button
              className={
                "bg-accent-foreground/90 hover:bg-accent-foreground text-background w-full shadow-xl"
              }
              disabled={isSubmitting || !form.formState.isValid}
              size="lg"
              type="submit"
            >
              {t("common:continue")}
              <Icon icon={"mdi:arrow-right"} />
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
};

export default CommunicationDataForm;
